import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import { 
  SportsHockey, 
  EmojiEvents, 
  TrendingUp, 
  Star,
  Timeline,
  Group 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiConfig } from '../config/api';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const theme = useTheme();

  const handleLogin = () => {
    window.location.href = apiConfig.auth.google;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 8 }}>
        {/* Hero Section */}
        <Box 
          textAlign="center" 
          mb={8}
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
            borderRadius: 4,
            p: 6,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
              transform: 'translateX(-100%)',
              animation: 'shimmer 3s infinite',
            },
            '@keyframes shimmer': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(100%)' },
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <SportsHockey sx={{ fontSize: 64, color: 'primary.main', mr: 2 }} />
            <Chip 
              label="UUSI KAUSI 2025-26" 
              color="secondary"
              variant="filled"
              sx={{ 
                position: 'absolute', 
                top: 20, 
                right: 20,
                fontWeight: 600,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                  '100%': { transform: 'scale(1)' },
                },
              }} 
            />
          </Box>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 800,
              mb: 2,
            }}
          >
            🏒 Liiga Ennustuspeli
          </Typography>
          <Typography variant="h5" color="text.secondary" paragraph sx={{ fontWeight: 500 }}>
            Ennusta Suomen jääkiekon tulokset ja kilpaile ystävien kanssa!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
            Testaa jääkiekkotuntemuksesi ennustamalla Liigan otteluiden tarkat tulokset. 
            Kerää pisteitä onnistuneista ennustuksista ja nouse tulostaulun kärkeen!
          </Typography>
          {!isAuthenticated && (
            <Button
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{ 
                mt: 3, 
                px: 6, 
                py: 2,
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Aloita Pelaaminen - Kirjaudu Googlella
            </Button>
          )}
        </Box>

        {/* Features */}
        <Grid container spacing={4} mb={8}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                textAlign: 'center',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box 
                  sx={{ 
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  }}
                >
                  <SportsHockey sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Ennusta Tulokset
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Arvaa tulevien Liiga-otteluiden tarkat tulokset ja ansaitse pisteitä onnistuneista ennustuksista
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                textAlign: 'center',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  borderColor: theme.palette.secondary.main,
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box 
                  sx={{ 
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  }}
                >
                  <EmojiEvents sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Kilpaile & Voita
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Kiipeä tulostaulun kärkeen ja näytä jääkiekkotuntemuksesi muille pelaajille
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                height: '100%', 
                textAlign: 'center',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.1)} 100%)`,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                  borderColor: theme.palette.success.main,
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box 
                  sx={{ 
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    borderRadius: '50%',
                    width: 80,
                    height: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                  }}
                >
                  <TrendingUp sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Seuraa Tilastoja
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  Tarkkaile tarkkuuttasi ja katso kuinka kehityt ajan myötä ennustajana
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Scoring System */}
        <Paper 
          sx={{ 
            p: 5, 
            mb: 6,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.default, 0.4)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          }}
        >
          <Box textAlign="center" mb={4}>
            <Star sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Pisteytyssysteemi
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Ansaitse pisteitä onnistuneista ennustuksista. Mitä tarkempi arvaus, sitä enemmän pisteitä!
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={4} textAlign="center">
              <Box 
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                  border: `2px solid ${alpha(theme.palette.error.main, 0.2)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    borderColor: theme.palette.error.main,
                  },
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'error.main', mb: 1 }}>0</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Väärä Arvaus</Typography>
                <Typography color="text.secondary" variant="body2">
                  Väärä voittajajoukkue tai tulos
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Box 
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                  border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    borderColor: theme.palette.warning.main,
                  },
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'warning.main', mb: 1 }}>1</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Oikea Voittaja</Typography>
                <Typography color="text.secondary" variant="body2">
                  Oikea joukkue voittaa, väärä tulos
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4} textAlign="center">
              <Box 
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    borderColor: theme.palette.success.main,
                  },
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 800, color: 'success.main', mb: 1 }}>3</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Tarkka Tulos</Typography>
                <Typography color="text.secondary" variant="body2">
                  Täydellinen ennustus! 🎯
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action */}
        {!isAuthenticated && (
          <Box 
            textAlign="center"
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              borderRadius: 4,
              p: 5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Group sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Valmis aloittamaan ennustamisen?
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
              Liity mukaan jännittävään jääkiekkoennustusyhteisöön ja testaa taitojasi!
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleLogin}
              sx={{ 
                px: 6, 
                py: 2,
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Kirjaudu Googlella
            </Button>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Home;
